import { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { Alert, Typography } from 'antd';
import {
  VGSCollectForm,
  useVGSCollectFormInstance,
  useVGSCollectState,
} from '@vgs/collect-js-react';

import SectionMessage from 'components/SectionMessage/SectionMessage';
import config from 'config';
import { VERIFY_CVV } from 'queries/cvvVerify';
import useTheme from 'hooks/useTheme';
import { SIZE, STATUS } from 'utilities/constants';

const { Text } = Typography;
const { CardSecurityCodeField } = VGSCollectForm;

const CvvFormContent = ({ hide, setFormSubmitHandler, onLoadingChange }) => {
  const { accountId, customerId } = useParams();
  const { tokens } = useTheme();
  const [cvvResultData, setCvvResultData] = useState(null);
  const [showVerifyFailure, setShowVerifyFailure] = useState(false);

  const [formState] = useVGSCollectState();
  const [formInstance] = useVGSCollectFormInstance();

  const formName = 'card-cvv';
  const fieldValue = formState?.[formName];

  const inputFieldCss = {
    border: `1px solid ${tokens.colorBorderSecondary}`,
    background: tokens.colorBgContainer,
    color: tokens.colorText,
    width: '100%',
    height: '32px',
    boxSizing: 'border-box',
    overflow: 'hidden',
    fontSize: 'var(--font-size-sm)',
    '&.invalid.touched': {
      color: tokens.colorError,
    },
  };

  const customizeValidationError = (errorMessage) => {
    if (errorMessage === 'is not a valid security code') {
      return 'must be 3 numbers';
    }
    return errorMessage;
  };

  const handleSubmit = () => {
    if (!formInstance || !fieldValue?.isValid) {
      return;
    }

    setShowVerifyFailure(false);
    onLoadingChange(true);

    formInstance.submit(
      config.VGS_GQL_PATH,
      {
        withCredentials: true,
        data: (values) => {
          return {
            query: VERIFY_CVV.loc?.source.body,
            variables: {
              accountId: accountId,
              customerId: customerId,
              cvv: values[formName],
              verifyPreviousValue: false,
            },
          };
        },
      },
      (_, response) => {
        if (response?.errors && response?.errors.length) {
          setShowVerifyFailure(true);
        } else {
          setCvvResultData(response?.data?.verifyCvv || null);
        }
        onLoadingChange(false);
      },
      (error) => {
        console.error('CVV verification error:', error);
        setShowVerifyFailure(true);
        onLoadingChange(false);
      },
    );
  };

  useEffect(() => {
    setFormSubmitHandler(handleSubmit, fieldValue?.isValid);
  }, [setFormSubmitHandler, fieldValue?.isValid]);

  const closeModal = () => {
    setCvvResultData(null);
    setShowVerifyFailure(false);
    onLoadingChange(false);
    hide();
  };

  const CVVValidated = () => {
    setTimeout(closeModal, 2500);

    return (
      <SectionMessage
        status={STATUS.SUCCESS}
        size={SIZE.SM}
        text="CVV has been verified!"
        cover={true}
        visible={cvvResultData?.isCvvVerified}
      />
    );
  };

  const CVVBlocked = () => (
    <Alert title="CVV verification is blocked!" type="error" />
  );

  const CVVUnverified = () => (
    <Alert
      title={`Unable to verify CVV. You may give the customer up to three tries to provide the correct CVV number.`}
      type="warning"
      showIcon
    />
  );

  return (
    <>
      <div style={{ height: '40px', overflow: 'hidden' }}>
        <VGSCollectForm
          vaultId={config.VGS_VAULT_ID}
          environment={config.VGS_ENV}
          cname={config.VGS_CNAME}
        >
          <CardSecurityCodeField
            name={formName}
            validations={['required', 'validCardSecurityCode']}
            placeholder="CVV Number"
            maxLength={3}
            hideValue={true}
            css={inputFieldCss}
            onUpdate={() => {
              if (cvvResultData) {
                setCvvResultData(null);
              }
            }}
          />
        </VGSCollectForm>

        {fieldValue?.isTouched &&
          !fieldValue?.isValid &&
          fieldValue?.errorMessages?.length > 0 && (
            <Text type="danger">
              CVV {customizeValidationError(fieldValue.errorMessages[0])}.
            </Text>
          )}
      </div>

      {cvvResultData != null &&
        (cvvResultData?.isCvvVerified ? (
          <CVVValidated />
        ) : cvvResultData?.isLockedOut ? (
          <CVVBlocked />
        ) : (
          <CVVUnverified numAttempts={cvvResultData?.remainingSubmissions} />
        ))}

      {showVerifyFailure ? (
        <Alert
          title="We've experienced an error while trying to verify the customer's CVV. Please try again."
          type="error"
        />
      ) : null}
    </>
  );
};

export default CvvFormContent;
